import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DejaVuSansBoldTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans-Bold.ttf';
import DejaVuSansTtf from 'dejavu-fonts-ttf/ttf/DejaVuSans.ttf';
import {
  buildBookingReservationsPdfModel,
  formatBookingReservationsPdfAmounts,
  type BookingReservationsPdfModelParams,
} from './bookingReservationsPdfModel';

interface JsPdfWithAutoTable extends jsPDF {
  lastAutoTable?: {
    finalY: number;
  };
}

const PDF_FONT_FAMILY = 'DejaVuSans';
const PDF_MARGIN_LEFT = 14;
const PDF_MARGIN_RIGHT = 14;
const TITLE_LINE_HEIGHT = 6;

let cachedRegularFontBinary: string | null = null;
let cachedBoldFontBinary: string | null = null;

const arrayBufferToBinaryString = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    let chunkBinary = '';
    for (let innerIndex = 0; innerIndex < chunk.length; innerIndex += 1) {
      chunkBinary += String.fromCharCode(chunk[innerIndex]);
    }
    binary += chunkBinary;
  }

  return binary;
};

const loadFontBinary = async (fontUrl: string): Promise<string> => {
  const response = await fetch(fontUrl);
  if (!response.ok) {
    throw new Error(`Nie udało się załadować fontu PDF (${response.status}).`);
  }

  const fontBuffer = await response.arrayBuffer();
  return arrayBufferToBinaryString(fontBuffer);
};

const ensureUnicodeFontsRegistered = async (doc: jsPDF): Promise<void> => {
  if (!cachedRegularFontBinary) {
    cachedRegularFontBinary = await loadFontBinary(DejaVuSansTtf);
  }

  if (!cachedBoldFontBinary) {
    cachedBoldFontBinary = await loadFontBinary(DejaVuSansBoldTtf);
  }

  doc.addFileToVFS('DejaVuSans.ttf', cachedRegularFontBinary);
  doc.addFont('DejaVuSans.ttf', PDF_FONT_FAMILY, 'normal');
  doc.addFileToVFS('DejaVuSans-Bold.ttf', cachedBoldFontBinary);
  doc.addFont('DejaVuSans-Bold.ttf', PDF_FONT_FAMILY, 'bold');
};

export const createBookingReservationsPdfBlob = (
  params: BookingReservationsPdfModelParams
): Promise<Blob> => {
  return createBookingReservationsPdfBlobInternal(params);
};

const createBookingReservationsPdfBlobInternal = async (
  params: BookingReservationsPdfModelParams
): Promise<Blob> => {
  const model = buildBookingReservationsPdfModel(params);
  const doc = new jsPDF() as JsPdfWithAutoTable;
  await ensureUnicodeFontsRegistered(doc);
  doc.setFont(PDF_FONT_FAMILY, 'normal');

  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PDF_MARGIN_LEFT - PDF_MARGIN_RIGHT;
  const titleLines = doc.splitTextToSize(model.title, contentWidth);
  doc.setFontSize(14);
  doc.text(titleLines, PDF_MARGIN_LEFT, 16);
  const termLabelY = 16 + titleLines.length * TITLE_LINE_HEIGHT + 2;

  doc.setFontSize(11);
  doc.text(model.termLabel, PDF_MARGIN_LEFT, termLabelY);

  autoTable(doc, {
    startY: termLabelY + 6,
    head: [['Lp', 'Nr faktury', 'Nabywca', 'Kwota']],
    body: model.rows.map((row) => [
      String(row.lp),
      row.invoiceNumber,
      row.buyer,
      formatBookingReservationsPdfAmounts(row.amounts),
    ]),
    styles: { fontSize: 10, cellPadding: 2.5, font: PDF_FONT_FAMILY },
    headStyles: { fontStyle: 'bold', font: PDF_FONT_FAMILY },
    columnStyles: {
      0: { halign: 'right', cellWidth: 12 },
      3: { halign: 'right', cellWidth: 35 },
    },
  });

  let summaryY = (doc.lastAutoTable?.finalY ?? termLabelY + 6) + 10;
  const pageHeight = doc.internal.pageSize.getHeight();
  if (summaryY > pageHeight - 14) {
    doc.addPage();
    summaryY = 20;
  }

  doc.setFontSize(11);
  doc.setFont(PDF_FONT_FAMILY, 'bold');
  doc.text(
    `Suma kwot z FV: ${formatBookingReservationsPdfAmounts(model.totals)}`,
    PDF_MARGIN_LEFT,
    summaryY
  );

  return doc.output('blob');
};

export const downloadBookingReservationsPdf = (blob: Blob, fileName: string): void => {
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error('Wygenerowany PDF rezerwacji jest pusty.');
  }

  const url = window.URL.createObjectURL(blob);

  try {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};
